import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMap } from '@angular/google-maps';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { NgModule } from '@angular/core';
import { NgxPaginationModule } from 'ngx-pagination';
import { RouterModule } from '@angular/router';
import { ShareModule } from 'ngx-sharebuttons';
import { TranslateModule } from '@ngx-translate/core';

import { CmsFooterComponent } from '~/component/cms-footer/cms-footer.component';
import { CoreModule } from '~/core/core.module';
import { TogglesModule } from '~/toggles/toggles.module';

import { ActivityCardComponent } from './activity-card/activity-card.component';
import { ActivityComponent } from './activity/activity.component';
import { ActivityDraftCardComponent } from './activity-draft-card/activity-draft-card.component';
import { ActivityHeadingComponent } from './activity/activity-heading/activity-heading.component';
import { ActivityReviewsComponent } from './activity/activity-reviews/activity-reviews.component';
import { ActivityReviewsDetailComponent } from './activity/activity-reviews-detail/activity-reviews-detail';
import { ActivityTimeslotListComponent } from './activity-timeslot-list/timeslot-list.component';
import { AddressComponent } from './form/address/address.component';
import { AlertModalComponent } from './modal/alert/alert.component';
import { BackLinkComponent } from './back-link/back-link.component';
import { BadgeComponent } from './badge/badge.component';
import { BasicHeaderLayoutComponent } from './layout/basic-header/basic-header.component';
import { BeveledPictureComponent } from './beveled-picture/beveled-picture.component';
import { CallToActionComponent } from './call-to-action/call-to-action.component';
import { CartComponent } from './header/cart/cart.component';
import { CheckoutSummaryComponent } from '~/checkout/components/shared/summary/summary.component';
import { ChipsComponent } from './chips/chips.component';
import { CmsPipedriveWebformErrorSectionComponent } from '../cms/components/cms-pipedrive-webform-error-section/cms-pipedrive-webform-error-section.component';
import { CollapseComponent } from './collapse/collapse.component';
import { DividerComponent } from './divider/divider.component';
import { FooterComponent } from './footer/footer.component';
import { FormAutocompleteComponent } from './form/autocomplete/autocomplete.component';
import { FormAutocompleteItemComponent } from './form/autocomplete/item/item.component';
import { FormAutocompleteListComponent } from './form/autocomplete/list/list.component';
import { FormAutocompleteTriggerComponent } from './form/autocomplete/trigger/trigger.component';
import { FormAutocompleteV2Component } from './form/autocomplete-v2/autocomplete-v2.component';
import { FormCheckboxComponent } from './form/checkbox/checkbox.component';
import { FormCheckboxGroupComponent } from './form/checkbox/group/group.component';
import { FormControlComponent } from './form/control/control.component';
import { FormDatetimeComponent } from './form/datetime/datetime.component';
import { FormDropdownComponent } from './form/dropdown/dropdown.component';
import { FormDropdownCountryComponent } from './form/dropdown/country/country.component';
import { FormDropdownItemComponent } from './form/dropdown/item/item.component';
import { FormDropdownListComponent } from './form/dropdown/list/list.component';
import { FormDropdownRegionComponent } from './form/dropdown/region/region.component';
import { FormDropdownTriggerComponent } from './form/dropdown/trigger/trigger.component';
import { FormDurationComponent } from './form/duration/duration.component';
import { FormImageComponent } from './form/image/image.component';
import { FormImageCropperComponent } from './form/image/cropper/cropper.component';
import { FormImageSelectActivityPhotoComponent } from './form/image/selects/activity-photo/activity-photo.component';
import { FormImageSelectHorizontalProfilePhotoComponent } from './form/image/selects/horizontal-profile-photo/horizontal-profile-photo.component';
import { FormImageSelectProfilePhotoComponent } from './form/image/selects/profile-photo/profile-photo.component';
import { FormInputComponent } from './form/input/input.component';
import { FormInputCounterComponent } from './form/input-counter/input-counter.component';
import { FormInputGroupComponent } from './form/input-group/input-group.component';
import { FormRadioButtonComponent } from './form/radio-button/radio-button.component';
import { FormRangeInputComponent } from './form/range-input/range-input.component';
import { FormTextareaComponent } from './form/textarea/textarea.component';
import { FormToggleSwitchComponent } from './form/toggle-switch/toggle-switch.component';
import { MapComponent } from './map/map.component';
import { HeaderComponent } from './header/header.component';
import { HeaderLoginComponent } from './header/login/login.component';
import { HeaderLoginMenuComponent } from './header/login/menu/menu.component';
import { HorizontalCardComponent } from './horizontal-card/horizontal-card.component';
import { IconComponent } from './icon/icon.component';
import { LogoComponent } from './logo/logo.component';
import { LoyaltyPointsCardComponent } from './loyalty-points-card/loyalty-points-card.component';
import { MobileFixedPanelComponent } from './mobile-fixed-panel/mobile-fixed-panel.component';
import { ModalComponent } from './modal/modal.component';
import { ModalEditTimeslotsComponent } from './modal-edit-timeslots/modal-edit-timeslots.component';
import { NavigationButtonComponent } from './buttons/navigation-button/navigation-button.component';
import { NavigationTabBarComponent } from './navigation-tab-bar/navigation-tab-bar.component';
import { NavigationTabBarConversionButtonComponent } from './navigation-tab-bar/buttons/conversion/conversion.component';
import { NavigationTabBarNavigationButtonComponent } from './navigation-tab-bar/buttons/navigation/navigation.component';
import { OfferCollectionComponent } from './offer-collection/offer-collection.component';
import { PaginationButtonsComponent } from './pagination-buttons/pagination-buttons.component';
import { PanelComponent } from './panel/panel.component';
import { RouterTabBarComponent } from './router-tab-bar/tab-bar.component';
import { SectionComponent } from './section/section.component';
import { SharerButtonsComponent } from './sharer-buttons/sharer-buttons.component';
import { SocialButtonComponent } from './social-button/social-button.component';
import { SocialButtonsComponent } from './social-buttons/social-buttons.component';
import { StepperTabBarComponent } from './stepper/tab-bar/tab-bar.component';
import { StepperTitleComponent } from './stepper/title/title.component';
import { SuccessLayoutComponent } from './layout/success/success.component';
import { SuccessOverlayComponent } from './success-overlay/success-overlay.component';
import { SummaryDetailComponent } from '~/checkout/components/shared/summary/summary-detail/summary-detail.component';
import { TabButtonsGroupComponent } from './tab-buttons-group/tab-buttons-group.component';
import { TabButtonsGroupItemComponent } from './tab-buttons-group/item/item.component';
import { TaxExcludedComponent } from '~/checkout/components/shared/summary/tax-excluded/tax-excluded.component';
import { TaxIncludedComponent } from '~/checkout/components/shared/summary/tax-included/tax-included.component';
import { TimelineComponent } from './timeline/timeline.component';
import { TimelineItemComponent } from './timeline/item/item.component';
import { TimelineListComponent } from './timeline/list/list.component';
import { TimeslotComponent } from './timeslot/timeslot.component';
import { TimeslotPricePreviewComponent } from './timeslot-price-preview/timeslot-price-preview.component';
import { TipsComponent } from './tips/tips.component';
import { ToPercentagePipe } from '~/core/pipes/to-percentage.pipe';
import { ToastComponent } from './toast/toast.component';
import { ToastsComponent } from './toasts/toasts.component';
import { UnauthorizedAccessComponent } from './unauthorized-access/unauthorized-access.component';
import { UploadListComponent } from './upload-list/upload-list.component';
import { VtmnPlayButtonComponent } from './buttons/vtmn-play-button/vtmn-play-button.component';
import { VtmnButtonComponent } from './buttons/vtmn-button/vtmn-button.component';
import { VtmnPlayIconComponent } from './vtmn-play-icon/vtmn-play-icon.component';
import { VtmnPlayIconButtonComponent } from './buttons/vtmn-play-icon-button/vtmn-play-icon-button.component';
import { VtmnButtonIconComponent } from './buttons/vtmn-button/vtmn-button-icon/vtmn-button-icon.component';
import { VtmnModalComponent } from './vtmn-modal/vtmn-modal.component';
import { VtmnRatingComponent } from './vtmn-rating/vtmn-rating.component';
import { VtmnStepperComponent } from './stepper/vtmn-stepper/vtmn-stepper.component';
import { TextToggleComponent } from '~/attendee/components/shared/text-toggle/text-toggle.component';
import { AngularSvgIconModule } from 'angular-svg-icon';

/**
 * Design system
 */
@NgModule({
  declarations: [
    ActivityCardComponent,
    ActivityComponent,
    ActivityDraftCardComponent,
    ActivityHeadingComponent,
    ActivityReviewsComponent,
    ActivityReviewsDetailComponent,
    ActivityTimeslotListComponent,
    AddressComponent,
    AlertModalComponent,
    BackLinkComponent,
    BadgeComponent,
    BasicHeaderLayoutComponent,
    BeveledPictureComponent,
    CallToActionComponent,
    CartComponent,
    CheckoutSummaryComponent,
    ChipsComponent,
    CmsFooterComponent,
    CmsPipedriveWebformErrorSectionComponent,
    CollapseComponent,
    DividerComponent,
    FooterComponent,
    FormAutocompleteComponent,
    FormAutocompleteItemComponent,
    FormAutocompleteListComponent,
    FormAutocompleteTriggerComponent,
    FormAutocompleteV2Component,
    FormCheckboxComponent,
    FormCheckboxGroupComponent,
    FormControlComponent,
    FormDatetimeComponent,
    FormDropdownComponent,
    FormDropdownCountryComponent,
    FormDropdownItemComponent,
    FormDropdownListComponent,
    FormDropdownRegionComponent,
    FormDropdownTriggerComponent,
    FormDurationComponent,
    FormImageComponent,
    FormImageCropperComponent,
    FormImageSelectActivityPhotoComponent,
    FormImageSelectHorizontalProfilePhotoComponent,
    FormImageSelectProfilePhotoComponent,
    FormInputComponent,
    FormInputCounterComponent,
    FormInputGroupComponent,
    FormRadioButtonComponent,
    FormRangeInputComponent,
    FormTextareaComponent,
    FormToggleSwitchComponent,
    MapComponent,
    HeaderComponent,
    HeaderLoginComponent,
    HeaderLoginMenuComponent,
    HorizontalCardComponent,
    IconComponent,
    LogoComponent,
    LoyaltyPointsCardComponent,
    MobileFixedPanelComponent,
    ModalComponent,
    ModalEditTimeslotsComponent,
    NavigationButtonComponent,
    NavigationTabBarComponent,
    NavigationTabBarConversionButtonComponent,
    NavigationTabBarNavigationButtonComponent,
    OfferCollectionComponent,
    PaginationButtonsComponent,
    PanelComponent,
    RouterTabBarComponent,
    SectionComponent,
    SharerButtonsComponent,
    SocialButtonComponent,
    SocialButtonsComponent,
    StepperTabBarComponent,
    StepperTitleComponent,
    SuccessLayoutComponent,
    SuccessOverlayComponent,
    SummaryDetailComponent,
    TabButtonsGroupComponent,
    TabButtonsGroupItemComponent,
    TaxExcludedComponent,
    TaxIncludedComponent,
    TextToggleComponent,
    TimelineComponent,
    TimelineItemComponent,
    TimelineListComponent,
    TimeslotComponent,
    TimeslotPricePreviewComponent,
    TipsComponent,
    ToPercentagePipe,
    ToastComponent,
    ToastsComponent,
    UnauthorizedAccessComponent,
    UploadListComponent,
    VtmnButtonComponent,
    VtmnPlayButtonComponent,
    VtmnButtonIconComponent,
    VtmnModalComponent,
    VtmnPlayIconButtonComponent,
    VtmnPlayIconComponent,
    VtmnRatingComponent,
    VtmnStepperComponent,
  ],
  exports: [
    ActivityCardComponent,
    ActivityComponent,
    ActivityDraftCardComponent,
    ActivityHeadingComponent,
    ActivityReviewsComponent,
    ActivityReviewsDetailComponent,
    ActivityTimeslotListComponent,
    AddressComponent,
    AlertModalComponent,
    BackLinkComponent,
    BadgeComponent,
    BasicHeaderLayoutComponent,
    BeveledPictureComponent,
    CallToActionComponent,
    CartComponent,
    CheckoutSummaryComponent,
    ChipsComponent,
    CmsFooterComponent,
    CmsPipedriveWebformErrorSectionComponent,
    CollapseComponent,
    DividerComponent,
    FooterComponent,
    FormAutocompleteComponent,
    FormAutocompleteV2Component,
    FormCheckboxComponent,
    FormCheckboxGroupComponent,
    FormControlComponent,
    FormDatetimeComponent,
    FormDropdownComponent,
    FormDropdownCountryComponent,
    FormDropdownRegionComponent,
    FormDurationComponent,
    FormImageComponent,
    FormImageSelectActivityPhotoComponent,
    FormInputComponent,
    FormInputCounterComponent,
    FormInputGroupComponent,
    FormRadioButtonComponent,
    FormRangeInputComponent,
    FormTextareaComponent,
    FormToggleSwitchComponent,
    MapComponent,
    HeaderComponent,
    HeaderLoginComponent,
    HeaderLoginMenuComponent,
    HorizontalCardComponent,
    IconComponent,
    LogoComponent,
    LoyaltyPointsCardComponent,
    MobileFixedPanelComponent,
    ModalComponent,
    ModalEditTimeslotsComponent,
    NavigationButtonComponent,
    NavigationTabBarComponent,
    NavigationTabBarConversionButtonComponent,
    NavigationTabBarNavigationButtonComponent,
    OfferCollectionComponent,
    PaginationButtonsComponent,
    PanelComponent,
    RouterTabBarComponent,
    SectionComponent,
    SharerButtonsComponent,
    SocialButtonComponent,
    SocialButtonsComponent,
    StepperTabBarComponent,
    StepperTitleComponent,
    SuccessLayoutComponent,
    SuccessOverlayComponent,
    SummaryDetailComponent,
    TabButtonsGroupComponent,
    TaxExcludedComponent,
    TaxIncludedComponent,
    TextToggleComponent,
    TimelineComponent,
    TimeslotComponent,
    TimeslotPricePreviewComponent,
    TipsComponent,
    ToPercentagePipe,
    ToastComponent,
    ToastsComponent,
    UnauthorizedAccessComponent,
    UploadListComponent,
    VtmnButtonComponent,
    VtmnPlayButtonComponent,
    VtmnPlayIconButtonComponent,
    VtmnPlayIconComponent,
    VtmnModalComponent,
    VtmnRatingComponent,
    VtmnStepperComponent,
  ],
  imports: [
    CommonModule,
    CoreModule,
    FormsModule,
    LocalizeRouterModule,
    NgxPaginationModule,
    ReactiveFormsModule,
    RouterModule,
    ShareModule,
    TogglesModule,
    TranslateModule,
    AngularSvgIconModule,
  ],
  providers: [GoogleMap],
})
export class ComponentModule {}
